import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Eye, ArrowSquareOut } from "@phosphor-icons/react";

const CATEGORY_LABELS = {
  evenements: "Événements",
  athletes: "Athlètes",
  entrainement: "Entraînement",
  nutrition: "Nutrition",
  mental: "Mental",
  circuit_quebec: "Circuit Québec",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-CA", { day: "numeric", month: "short", year: "numeric" });
  } catch { return iso; }
}

export default function BlogAdmin() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/blog/posts")
      .then(r => setPosts(r.data || []))
      .catch(() => toast.error("Échec du chargement"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = posts.filter(p => filter === "all" || p.status === filter);

  const remove = async (post) => {
    if (!window.confirm(`Supprimer l'article "${post.title}" ?`)) return;
    try {
      await api.delete(`/admin/blog/posts/${post.id}`);
      toast.success("Article supprimé");
      load();
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="ticker text-[#D90429] mb-2">JOURNAL DU KRU</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight">Blog · administration</h1>
        </div>
        <Link to="/app/blog/nouveau" data-testid="blog-admin-new-btn" className="hard-border px-5 py-3 ticker bg-[#D90429] text-white border-[#D90429] hover:bg-black hover:border-black flex items-center gap-2">
          <Plus size={16} /> Nouvel article
        </Link>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="blog-admin-filter">
        {[
          { id: "all", label: "Tous" },
          { id: "publie", label: "Publiés" },
          { id: "brouillon", label: "Brouillons" },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`blog-admin-filter-${f.id}`}
            className={`px-4 py-2 ticker hard-border ${filter === f.id ? "bg-foreground text-background" : "hover:bg-secondary"}`}
          >
            {f.label} ({f.id === "all" ? posts.length : posts.filter(p => p.status === f.id).length})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-muted-foreground ticker">Chargement…</div>
      ) : filtered.length === 0 ? (
        <div className="hard-border p-12 text-center" data-testid="blog-admin-empty">
          <div className="text-xl font-bold mb-2">Aucun article</div>
          <div className="text-muted-foreground text-sm mb-6">Crée ton premier article pour publier sur le blog.</div>
          <Link to="/app/blog/nouveau" className="hard-border inline-flex px-5 py-3 ticker bg-[#D90429] text-white border-[#D90429] hover:bg-black hover:border-black items-center gap-2">
            <Plus size={16} /> Créer un article
          </Link>
        </div>
      ) : (
        <div className="hard-border" data-testid="blog-admin-list">
          {filtered.map(p => (
            <div key={p.id} data-testid={`blog-admin-row-${p.id}`} className="px-5 py-4 hard-border-b last:border-b-0 flex items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className={`ticker text-[10px] px-2 py-0.5 ${p.status === "publie" ? "bg-[#D90429] text-white" : "bg-secondary text-muted-foreground"}`}>
                    {p.status === "publie" ? "PUBLIÉ" : "BROUILLON"}
                  </span>
                  <span className="ticker text-[10px] text-muted-foreground">{CATEGORY_LABELS[p.category] || p.category}</span>
                </div>
                <div className="font-semibold truncate">{p.title}</div>
                <div className="text-xs text-muted-foreground mt-1 truncate">/{p.slug}</div>
              </div>
              <div className="text-xs text-muted-foreground hidden md:flex items-center gap-3">
                <span className="flex items-center gap-1" title="Vues" data-testid={`blog-admin-views-${p.id}`}>
                  <Eye size={12} /> {p.views_count || 0}
                </span>
                <span>
                  {p.status === "publie" ? "Publié " + formatDate(p.published_at) : "Modifié " + formatDate(p.updated_at)}
                </span>
              </div>
              <div className="flex items-center gap-1">
                {p.status === "publie" && (
                  <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" data-testid={`blog-admin-view-${p.id}`} className="p-2 hover:bg-secondary" title="Voir public">
                    <ArrowSquareOut size={16} />
                  </a>
                )}
                <Link to={`/app/blog/${p.id}/editer`} data-testid={`blog-admin-edit-${p.id}`} className="p-2 hover:bg-secondary" title="Modifier">
                  <PencilSimple size={16} />
                </Link>
                <button onClick={() => remove(p)} data-testid={`blog-admin-delete-${p.id}`} className="p-2 hover:bg-[#D90429] hover:text-white" title="Supprimer">
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
